// src/pages/dashboard/branch/analytics/components/QuickDatePresets.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

export type DatePreset = "today" | "yesterday" | "last7days" | "last30days" | "thisMonth" | "lastMonth" | "thisYear";

interface QuickDatePresetsProps {
    activePreset: DatePreset | null;
    onPresetSelect: (preset: DatePreset) => void;
    disabled?: boolean;
}

interface PresetOption {
    id: DatePreset;
    label: string;
}



export const getPresetDates = (preset: DatePreset): { start: Date; end: Date } => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const end = new Date(today);
    let start = new Date(today);

    switch (preset) {
        case "today":
            // Both start and end are today
            break;

        case "yesterday":
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() - 1);
            break;

        case "last7days":
            start.setDate(start.getDate() - 6);
            break;

        case "last30days":
            start.setDate(start.getDate() - 29);
            break;

        case "thisMonth":
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            break;

        case "lastMonth":
            start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            end.setDate(0); // Last day of previous month
            break;

        // case "thisYear":
        //     start = new Date(today.getFullYear(), 0, 1);
        //     break;
    }

    return { start, end };
};

const QuickDatePresets: React.FC<QuickDatePresetsProps> = ({
    activePreset,
    onPresetSelect,
    disabled = false,
}) => {
    const { t } = useTranslation();

    const presets: { id: DatePreset; label: string }[] = [
        { id: "today", label: t("analytics.today", "Today") },
        { id: "yesterday", label: t("analytics.yesterday", "Yesterday") },
        { id: "last7days", label: t("analytics.last7Days", "Last 7 Days") },
        { id: "last30days", label: t("analytics.last30Days", "Last 30 Days") },
        { id: "thisMonth", label: t("analytics.thisMonth", "This Month") },
        { id: "lastMonth", label: t("analytics.lastMonth", "Last Month") },
    ];

    return (
        <div className="flex flex-wrap justify-center gap-2">
            {presets.map((preset, index) => {
                const isActive = activePreset === preset.id;

                return (
                    <motion.button
                        key={preset.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => onPresetSelect(preset.id as DatePreset)}
                        disabled={disabled}
                        className={`
                            px-3 py-1.5 rounded-lg text-xs font-medium
                            transition-all duration-200 
                            disabled:opacity-50 disabled:cursor-not-allowed
                            ${isActive
                                ? "bg-primary text-white shadow-lg shadow-primary/25"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                            }
                        `}
                    >
                        {preset.label}
                    </motion.button>
                );
            })}
        </div>
    );
};

export default QuickDatePresets;
